import avatarSrc from "../../helpers/avatarSrc";
import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CreateIcon from "@material-ui/icons/Create";
import PersonRoundedIcon from "@material-ui/icons/PersonRounded";
import NotesRoundedIcon from "@material-ui/icons/NoteRounded";
import SmartToyRoundedIcon from "@material-ui/icons/EmojiObjects";
import FlightTakeoffRoundedIcon from "@material-ui/icons/FlightTakeoff";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import { i18n } from "../../translate/i18n";

import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import ContactProfile from "../ContactProfile";
import ContactChannelHub from "../ContactChannelHub";
import CustomerJourney from "../CustomerJourney";
import DevicesOtherIcon from "@material-ui/icons/DevicesOther";
import TimelineIcon from "@material-ui/icons/Timeline";
import MemoryIcon from "@material-ui/icons/Memory";
import PhoneIcon from "@material-ui/icons/Phone";
import CustomerMemory from "../CustomerMemory";
import VoiceCallHistory from "../VoiceCallHistory";
import VoiceCollection from "../VoiceCollection";
import CommunicationTimeline from "../CommunicationTimeline";
import ErrorBoundary from "../ErrorBoundary";
import { useVoice } from "../../context/Voice/VoiceContext";
import TicketInsight from "../../aios/context/TicketInsight";
import isNativeApp from "../../utils/isNativeApp";

const drawerWidth = 340;

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === "dark";
	const surface = isDark ? "#131614" : "#ffffff";
	const subtleBg = isDark ? "rgba(255,255,255,0.04)" : "#f6faf7";
	const borderColor = isDark
		? "rgba(255,255,255,0.06)"
		: "rgba(0,0,0,0.06)";
	const textPrimary = isDark ? "#e6f7ec" : "#102b1f";
	const textMuted = isDark ? "rgba(230,247,236,0.55)" : "#5f6f6a";

	return {
		drawer: {
			width: drawerWidth,
			flexShrink: 0,
			// On phones the 340px fixed-width drawer was either
			// overflowing the viewport (<360px screens) or eating
			// 80-95% of it and covering the chat. Switch to 100% width
			// so the drawer takes the whole screen — operators use it
			// as a modal-style detail panel on mobile anyway.
			//
			// When CLOSED on phones the wrapper used to keep its 100%
			// width slot, which combined with Ticket.mainWrapper's
			// negative drawer-slot margin produced a half-rendered
			// chat (Raviv mobile screenshot 2026-05-18). Collapse the
			// wrapper to 0 when closed so the chat panel takes the
			// full viewport; it expands back to 100% on open.
			// Don't animate `width` — it's a layout property, so every
			// frame relayouts the chat pane behind the drawer. Snap the
			// wrapper instead and let the drawer Paper's own transform
			// transition carry the visual slide.
			[theme.breakpoints.down("xs")]: {
				width: ({ open }) => (open ? "100%" : 0),
			},
		},
		drawerPaper: {
			width: drawerWidth,
			display: "flex",
			background: surface,
			// Use logical inline-start so the border lands on the
			// correct visual edge in both LTR and RTL (left in LTR,
			// right in RTL). Previously hardcoded `borderLeft` put
			// the hairline on the wrong side of the drawer in Hebrew.
			borderInlineStart: `1px solid ${borderColor}`,
			[theme.breakpoints.down("xs")]: {
				width: "100%",
				borderInlineStart: "none",
			},
		},
		header: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: "14px 16px",
			borderBottom: `1px solid ${borderColor}`,
			position: "relative",
			background: surface,
			"&::before": {
				content: '""',
				position: "absolute",
				top: 0,
				left: 0,
				right: 0,
				height: 2,
				background:
					"linear-gradient(90deg, #25d366 0%, #128c7e 60%, transparent 100%)",
				opacity: isDark ? 0.55 : 0.7,
			},
		},
		headerTitle: {
			fontSize: "0.95rem",
			fontWeight: 700,
			color: textPrimary,
			letterSpacing: 0.2,
		},
		closeBtn: {
			color: textMuted,
			// size="small" renders ~30px — bump to a 44px touch target on phones
			// (matches the layout drawer-chevron fix from the 2026-06-30 audit).
			[theme.breakpoints.down("xs")]: { width: 44, height: 44 },
		},
		content: {
			display: "flex",
			flexDirection: "column",
			padding: 12,
			gap: 12,
			height: "100%",
			overflowY: "auto",
			...theme.scrollbarStyles,
		},

		// Generic FieldCard-style section block — used for Notes,
		// Chatbot notes, Other information.
		section: {
			background: surface,
			border: `1px solid ${borderColor}`,
			borderRadius: 14,
			padding: 14,
			display: "flex",
			flexDirection: "column",
			gap: 10,
		},
		sectionHeader: {
			display: "flex",
			alignItems: "center",
			gap: 10,
		},
		sectionIconPill: {
			width: 32,
			height: 32,
			borderRadius: 10,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			background: isDark
				? "linear-gradient(135deg, rgba(46,227,122,0.18), rgba(18,140,126,0.16))"
				: "linear-gradient(135deg, rgba(37,211,102,0.14), rgba(18,140,126,0.10))",
			color: isDark ? "#7df0a8" : "#128c7e",
			"& svg": { fontSize: 18 },
		},
		sectionTitle: {
			fontSize: "0.88rem",
			fontWeight: 700,
			color: textPrimary,
			letterSpacing: 0.2,
		},

		// Contact identity block — avatar + name + phone/email + edit btn
		contactHero: {
			background: surface,
			border: `1px solid ${borderColor}`,
			borderRadius: 14,
			padding: "18px 14px",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 10,
		},
		contactAvatarWrap: {
			position: "relative",
		},
		contactAvatar: {
			width: 80,
			height: 80,
			fontWeight: 700,
			fontSize: "1.6rem",
			border: isDark
				? "3px solid rgba(46,227,122,0.20)"
				: "3px solid rgba(37,211,102,0.22)",
			boxShadow: isDark
				? "0 6px 18px rgba(0,0,0,0.4)"
				: "0 6px 18px rgba(15,23,42,0.08)",
		},
		contactName: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			fontWeight: 700,
			fontSize: "1.05rem",
			color: textPrimary,
			cursor: "pointer",
			"& svg": {
				fontSize: 15,
				color: textMuted,
				transition: "color 0.15s ease",
			},
			"&:hover svg": { color: isDark ? "#7df0a8" : "#128c7e" },
		},
		contactPhone: {
			fontSize: "0.85rem",
			fontWeight: 600,
			color: isDark ? "#7df0a8" : "#128c7e",
			// Long emails/usernames have no natural break points and
			// overflowed the 340px drawer on narrow phones.
			maxWidth: "100%",
			overflowWrap: "anywhere",
			"& a": { color: "inherit", textDecoration: "none" },
		},
		contactEmail: {
			fontSize: "0.78rem",
			color: textMuted,
			maxWidth: "100%",
			overflowWrap: "anywhere",
			"& a": { color: "inherit", textDecoration: "none" },
		},
		editBtn: {
			marginTop: 4,
			borderRadius: 10,
			padding: "6px 18px",
			fontSize: "0.82rem",
			fontWeight: 600,
			textTransform: "none",
			color: isDark ? "#7df0a8" : "#128c7e",
			border: isDark
				? "1px solid rgba(46,227,122,0.28)"
				: "1px solid rgba(37,211,102,0.32)",
			"&:hover": {
				background: isDark
					? "rgba(46,227,122,0.08)"
					: "rgba(37,211,102,0.08)",
			},
		},

		// JSON / text rendering for chatbot notes
		chatbotNoteBox: {
			fontSize: 13,
			padding: 12,
			background: subtleBg,
			borderRadius: 10,
			border: `1px solid ${borderColor}`,
			whiteSpace: "pre-wrap",
			color: textPrimary,
		},
		travelRow: {
			padding: "4px 0",
			display: "flex",
			gap: 8,
			alignItems: "flex-start",
		},
		travelLabel: {
			fontWeight: 700,
			minWidth: 110,
			color: textMuted,
			fontSize: 12,
		},
		travelValue: {
			fontSize: 12,
			color: textPrimary,
			flex: 1,
		},
		matchedOffersTitle: {
			fontSize: "0.78rem",
			fontWeight: 700,
			color: textPrimary,
			marginTop: 10,
			marginBottom: 6,
		},
		matchedOffer: {
			fontSize: 12,
			padding: "6px 10px",
			background: isDark
				? "rgba(46,227,122,0.10)"
				: "rgba(37,211,102,0.10)",
			border: isDark
				? "1px solid rgba(46,227,122,0.22)"
				: "1px solid rgba(37,211,102,0.22)",
			borderRadius: 8,
			marginBottom: 4,
			color: textPrimary,
		},
		// Extra info entry
		extraInfoItem: {
			padding: 10,
			background: subtleBg,
			borderRadius: 10,
			border: `1px solid ${borderColor}`,
			"& .MuiInputLabel-root": {
				color: textMuted,
				fontSize: "0.72rem",
				fontWeight: 600,
				textTransform: "uppercase",
				letterSpacing: 0.3,
			},
		},
	};
});

const ContactDrawer = ({
	open,
	handleDrawerClose,
	contact,
	ticket,
	loading,
}) => {
	const classes = useStyles({ open });

	// Voice module gate (returns {} outside a VoiceProvider → treated as off).
	const voice = useVoice();
	const voiceOn = Boolean(voice && voice.status && voice.status.enabled);

	const [modalOpen, setModalOpen] = useState(false);
	const [openForm, setOpenForm] = useState(false);

	useEffect(() => {
		setOpenForm(false);
	}, [open, contact]);

	// Localised label table for the structured travel-request JSON
	// surfaced from the AI chatbot. Labels are pulled from i18n so the
	// drawer respects the agent's language (was previously hardcoded
	// Hebrew with emojis only).
	const travelLabelMap = {
		destination: i18n.t("contactDrawer.travelRequest.fields.destination"),
		country: i18n.t("contactDrawer.travelRequest.fields.country"),
		productType: i18n.t("contactDrawer.travelRequest.fields.productType"),
		familyComposition: i18n.t("contactDrawer.travelRequest.fields.familyComposition"),
		adults: i18n.t("contactDrawer.travelRequest.fields.adults"),
		children: i18n.t("contactDrawer.travelRequest.fields.children"),
		childrenAges: i18n.t("contactDrawer.travelRequest.fields.childrenAges"),
		checkInDate: i18n.t("contactDrawer.travelRequest.fields.checkInDate"),
		checkOutDate: i18n.t("contactDrawer.travelRequest.fields.checkOutDate"),
		nights: i18n.t("contactDrawer.travelRequest.fields.nights"),
		boardBasis: i18n.t("contactDrawer.travelRequest.fields.boardBasis"),
		budget: i18n.t("contactDrawer.travelRequest.fields.budget"),
		currency: i18n.t("contactDrawer.travelRequest.fields.currency"),
		preferences: i18n.t("contactDrawer.travelRequest.fields.preferences"),
	};
	const boardLabels = {
		all_inclusive: i18n.t("contactDrawer.travelRequest.board.all_inclusive"),
		full_board: i18n.t("contactDrawer.travelRequest.board.full_board"),
		half_board: i18n.t("contactDrawer.travelRequest.board.half_board"),
		bed_breakfast: i18n.t("contactDrawer.travelRequest.board.bed_breakfast"),
		room_only: i18n.t("contactDrawer.travelRequest.board.room_only"),
	};
	const productLabels = {
		flight_hotel: i18n.t("contactDrawer.travelRequest.product.flight_hotel"),
		flight_only: i18n.t("contactDrawer.travelRequest.product.flight_only"),
		hotel_only: i18n.t("contactDrawer.travelRequest.product.hotel_only"),
	};

	const renderChatbotNotes = () => {
		if (!ticket?.notes) return null;
		const extractJSON = (str) => {
			try {
				return JSON.parse(str);
			} catch {}
			const start = str.indexOf("{");
			if (start < 0) return null;
			let depth = 0;
			let inStr = false;
			let esc = false;
			for (let i = start; i < str.length; i++) {
				const c = str[i];
				if (esc) {
					esc = false;
					continue;
				}
				if (c === "\\" && inStr) {
					esc = true;
					continue;
				}
				if (c === '"' && !esc) {
					inStr = !inStr;
					continue;
				}
				if (inStr) continue;
				if (c === "{" || c === "[") depth++;
				if (c === "}" || c === "]") depth--;
				if (depth === 0) {
					try {
						return JSON.parse(str.substring(start, i + 1));
					} catch {
						return null;
					}
				}
			}
			return null;
		};
		const parsed = extractJSON(ticket.notes);
		const tr = parsed?.travelRequest;

		// Fallback: notes are not structured travel-request JSON, render
		// them as plain text so anything the bot wrote is still visible.
		if (!tr) {
			return (
				<div className={classes.section}>
					<div className={classes.sectionHeader}>
						<span className={classes.sectionIconPill}>
							<SmartToyRoundedIcon />
						</span>
						<span className={classes.sectionTitle}>
							{i18n.t("contactDrawer.chatbotNotes.title")}
						</span>
					</div>
					<div className={classes.chatbotNoteBox}>{ticket.notes}</div>
				</div>
			);
		}

		// Structured travel request
		return (
			<div className={classes.section}>
				<div className={classes.sectionHeader}>
					<span className={classes.sectionIconPill}>
						<FlightTakeoffRoundedIcon />
					</span>
					<span className={classes.sectionTitle}>
						{i18n.t("contactDrawer.travelRequest.title")}
					</span>
				</div>
				<div className={classes.chatbotNoteBox}>
					{Object.entries(travelLabelMap).map(([key, label]) => {
						let val = tr[key];
						if (val === undefined || val === null || val === "") return null;
						if (key === "boardBasis") val = boardLabels[val] || val;
						if (key === "productType") val = productLabels[val] || val;
						if (Array.isArray(val)) val = val.join(", ");
						if (key === "budget" && tr.currency) {
							val = `${Number(val).toLocaleString()} ${tr.currency}`;
						}
						if (key === "currency") return null;
						return (
							<div key={key} className={classes.travelRow}>
								<span className={classes.travelLabel}>{label}:</span>
								<span className={classes.travelValue}>{String(val)}</span>
							</div>
						);
					})}
				</div>
				{parsed?.matchedOffers?.length > 0 && (
					<div>
						<div className={classes.matchedOffersTitle}>
							{i18n.t("contactDrawer.travelRequest.matchedOffers", {
								count: parsed.matchedOffers.length,
							})}
						</div>
						{parsed.matchedOffers.map((o, i) => (
							<div key={i} className={classes.matchedOffer}>
								<strong>{o.title || o.destination}</strong>
								{o.finalPrice ? ` — $${o.finalPrice.toLocaleString()}` : ""}
								{o.matchScore ? ` (${i18n.t("contactDrawer.travelRequest.score")}: ${o.matchScore})` : ""}
							</div>
						))}
					</div>
				)}
			</div>
		);
	};

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<span className={classes.headerTitle}>
					{i18n.t("contactDrawer.header")}
				</span>
				<IconButton
					size="small"
					onClick={handleDrawerClose}
					className={classes.closeBtn}
				>
					<CloseIcon />
				</IconButton>
			</div>

			{/* Mount the heavy sections only while the drawer is actually
			    shown — variant="persistent" keeps this subtree mounted even
			    when closed, so without the `open` guard ContactChannelHub /
			    ContactNotes / CustomerJourney (+voice sections) fire their
			    fetches + socket subscriptions behind an invisible (width:0
			    on mobile) drawer, stealing main-thread time from the chat. */}
			{!open ? null : loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					{/* Contact identity */}
					<div className={classes.contactHero}>
						<span className={classes.contactAvatarWrap}>
							<Avatar
								className={classes.contactAvatar}
								src={avatarSrc(contact.profilePicUrl)}
								alt="contact_image"
								style={{
									backgroundColor: generateColor(contact?.number),
									color: "white",
								}}
							>
								{getInitials(contact?.name)}
							</Avatar>
						</span>
						<span
							className={classes.contactName}
							onClick={() => setOpenForm(true)}
						>
							{contact.name}
							<CreateIcon />
						</span>
						{/* 2026-06-11: omnichannel contacts store an fb_/ig_/tg_ id in
						    `number` — never render it as a clickable tel: phone. The
						    channel is already shown via the badge/icon.
						    2026-07-01: wau_ = WhatsApp Username/BSUID (no phone) — same
						    rule; the @username below is shown instead. */}
						{contact.number && !/^(fb_|ig_|tg_|wau_)/.test(contact.number) && (
							<span className={classes.contactPhone}>
								<Link href={`tel:${contact.number}`}>{contact.number}</Link>
							</span>
						)}
						{/* WhatsApp @username — 2026 feature; only render when set. */}
						{contact.whatsappUsername && (
							<span
								className={classes.contactPhone}
								style={{ color: "#25D366", fontWeight: 600 }}
							>
								@{contact.whatsappUsername}
							</span>
						)}
						{contact.email && (
							<span className={classes.contactEmail}>
								<Link href={`mailto:${contact.email}`}>{contact.email}</Link>
							</span>
						)}
						<Button
							className={classes.editBtn}
							onClick={() => setModalOpen(!openForm)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
						{contact.id && openForm && (
							<ContactForm
								initialContact={contact}
								onCancel={() => setOpenForm(false)}
							/>
						)}
					</div>

					{/* Customer Intelligence Profile — GoDeal-only feature.
					    Travel-vertical AI extracts a structured profile +
					    ready-to-book summary from the conversation. Other
					    tenants don't have the backend pipeline configured,
					    so the section would just sit there saying "Profile
					    will be built automatically" forever. Matches the
					    same companyId === "1" gate used in MainListItems
					    for other GoDeal-only nav entries. */}
					{String(localStorage.getItem("companyId")) === "1" && (
						<ContactProfile
							profile={contact?.profile}
							ticketId={ticket?.id}
							contactId={contact?.id}
						/>
					)}

					{/* Slice-8 Gate-3 — Contextual Brain: this customer.
					    Read-only Slice-6 intelligence projection; hides itself
					    on 403 and on native. Additive — existing sections
					    untouched. */}
					{!isNativeApp() && contact?.id && (
						<ErrorBoundary label="TicketInsight" resetKey={contact.id}>
							<TicketInsight contactId={contact.id} />
						</ErrorBoundary>
					)}

					{/* ─── Communication Intelligence (Voice) sections ───
					    Gated by the per-company Voice flag; render nothing when
					    Voice is off. Additive — existing sections are untouched. */}
					{voiceOn && contact?.id && (
						<div className={classes.section}>
							<div className={classes.sectionHeader}>
								<span className={classes.sectionIconPill}>
									<MemoryIcon />
								</span>
								<span className={classes.sectionTitle}>
									{i18n.t("voice.section.memory")}
								</span>
							</div>
							<ErrorBoundary label="CustomerMemory" resetKey={contact.id}>
								<CustomerMemory contactId={contact.id} ticketId={ticket?.id} />
							</ErrorBoundary>
						</div>
					)}

					{voiceOn && contact?.id && (
						<div className={classes.section}>
							<div className={classes.sectionHeader}>
								<span className={classes.sectionIconPill}>
									<PhoneIcon />
								</span>
								<span className={classes.sectionTitle}>
									{i18n.t("voice.section.calls")}
								</span>
							</div>
							<ErrorBoundary label="VoiceCallHistory" resetKey={contact.id}>
								<VoiceCallHistory contactId={contact.id} />
							</ErrorBoundary>
						</div>
					)}

					{voiceOn && contact?.id && (
						<div className={classes.section}>
							<div className={classes.sectionHeader}>
								<span className={classes.sectionIconPill}>
									<InfoOutlinedIcon />
								</span>
								<span className={classes.sectionTitle}>
									{i18n.t("voice.section.collection")}
								</span>
							</div>
							<ErrorBoundary label="VoiceCollection" resetKey={contact.id}>
								<VoiceCollection contactId={contact.id} ticketId={ticket?.id} />
							</ErrorBoundary>
						</div>
					)}

					{/* Phase 3.6 — Customer Channel Hub */}
					<div className={classes.section}>
						<div className={classes.sectionHeader}>
							<span className={classes.sectionIconPill}>
								<DevicesOtherIcon />
							</span>
							<span className={classes.sectionTitle}>{i18n.t("contactDrawerSections.communicationChannels")}</span>
						</div>
						{contact?.id && (
							<ContactChannelHub
								contactId={contact.id}
								companyId={contact.companyId || Number(localStorage.getItem("companyId"))}
							/>
						)}
					</div>

					{/* Contact notes */}
					<div className={classes.section}>
						<div className={classes.sectionHeader}>
							<span className={classes.sectionIconPill}>
								<NotesRoundedIcon />
							</span>
							<span className={classes.sectionTitle}>
								{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
							</span>
						</div>
						<ContactNotes ticket={ticket} />
					</div>

					{/* Chatbot-extracted notes (free-text or structured travel
					    request) — only when present */}
					{renderChatbotNotes()}

					{/* Phase 3.11 — Customer Journey */}
					<div className={classes.section}>
						<div className={classes.sectionHeader}>
							<span className={classes.sectionIconPill}>
								<TimelineIcon />
							</span>
							<span className={classes.sectionTitle}>{i18n.t("contactDrawerSections.customerJourney")}</span>
						</div>
						{contact?.id && (
							voiceOn ? (
								<ErrorBoundary label="CommunicationTimeline" resetKey={contact.id}>
									<CommunicationTimeline contactId={contact.id} />
								</ErrorBoundary>
							) : (
								<CustomerJourney
									contactId={contact.id}
									companyId={contact.companyId || Number(localStorage.getItem("companyId"))}
								/>
							)
						)}
					</div>

					{/* Extra info block */}
					<div className={classes.section}>
						<div className={classes.sectionHeader}>
							<span className={classes.sectionIconPill}>
								<InfoOutlinedIcon />
							</span>
							<span className={classes.sectionTitle}>
								{i18n.t("contactDrawer.extraInfo")}
							</span>
						</div>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						/>
						{(contact?.extraInfo || []).map((info) => (
							<div key={info.id} className={classes.extraInfoItem}>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</div>
						))}
					</div>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
